import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Plus, Trash2, Loader2, UserCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';

const Household = () => {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [members, setMembers] = useState([]);
  const [name, setName] = useState('');
  const [relation, setRelation] = useState('');
  const [busy, setBusy] = useState(false);
  const active = localStorage.getItem('wellth_active_member') || '';

  useEffect(() => {
    if (loading) return;
    if (!user) { nav('/login'); return; }
    api.get('/household/members').then(({ data }) => setMembers(data.members));
  }, [user, loading, nav]);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post('/household/members', { name, relation });
      setMembers((m) => [...m, data]);
      setName(''); setRelation('');
      toast.success(`${data.name} added to your household`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add'); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/household/members/${id}`); setMembers((m) => m.filter((x) => x.id !== id)); }
    catch (e) { toast.error('Cannot remove'); }
  };

  const setActive = (m) => {
    localStorage.setItem('wellth_active_member', m.id);
    localStorage.setItem('wellth_active_member_name', m.name);
    toast.success(`Now tracking as ${m.name}`);
    window.dispatchEvent(new Event('wellth-member-changed'));
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-16" data-testid="household-container">
      <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#7E9979]">Household</span>
      <h1 className="mt-3 font-serif-display text-5xl text-[#1A2421] font-medium leading-tight">Everyone under one roof.</h1>
      <p className="mt-3 text-[#4A5A53] font-light max-w-xl">
        Add family members so the whole household stays on one clean plate. Each member gets their own tracker and avatar; you switch profiles anytime from the top bar.
      </p>

      <ul className="mt-10 space-y-3">
        {members.map((m) => (
          <li key={m.id} className="bg-white border border-[#E5E1D8] rounded-2xl p-5 flex items-center justify-between" data-testid="household-member">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#F2EFE9] flex items-center justify-center text-[#4A7C59] font-semibold">
                {m.name.slice(0, 1).toUpperCase()}
              </div>
              <div>
                <div className="font-medium text-[#1A2421]">{m.name} {active === m.id && <span className="text-xs text-[#4A7C59] ml-2">· active</span>}</div>
                <div className="text-xs text-[#4A5A53]">{m.relation || (m.is_self ? 'You' : 'Household member')}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={() => setActive(m)} variant="outline" size="sm" className="rounded-full border-[#E5E1D8] gap-1.5" data-testid="household-set-active">
                <UserCheck className="w-4 h-4" strokeWidth={1.5} /> Track as
              </Button>
              {!m.is_self && (
                <Button onClick={() => remove(m.id)} variant="ghost" size="icon" className="rounded-full text-[#4A5A53] hover:text-[#C05746]" data-testid="household-remove">
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>

      <form onSubmit={add} className="mt-8 bg-white border border-[#E5E1D8] rounded-2xl p-6">
        <label className="text-xs tracking-[0.2em] uppercase font-semibold text-[#4A5A53]">Add a family member</label>
        <div className="mt-3 flex flex-wrap gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className="flex-1 min-w-[160px] h-12 bg-white border-[#E5E1D8] rounded-full" data-testid="household-name-input" />
          <Input value={relation} onChange={(e) => setRelation(e.target.value)} placeholder="Relation (Spouse, Child…)" className="flex-1 min-w-[160px] h-12 bg-white border-[#E5E1D8] rounded-full" data-testid="household-relation-input" />
          <Button disabled={busy || !name.trim()} type="submit" className="rounded-full bg-[#4A7C59] hover:bg-[#3E6B4F] h-12 gap-2 px-5" data-testid="household-add-button">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4" strokeWidth={1.5} /> Add</>}
          </Button>
        </div>
      </form>

      <p className="mt-6 text-xs text-[#4A5A53] font-light italic max-w-xl">
        Household mode is included with Individual and Business plans. Cross-contamination isn&rsquo;t just bacteria &mdash; it&rsquo;s habits.
      </p>
    </div>
  );
};

export default Household;
